import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Eye, Clock, Users, Mail, Plus, X, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

const RECIPIENT_TYPES = [
  { value: 'all',          label: 'Todos os alunos ativos',      desc: '' },
  { value: 'contacts',     label: 'Contatos do site',            desc: '' },
  { value: 'all_contacts', label: 'Alunos + Contatos do site',   desc: '' },
  { value: 'students',     label: 'Alunos específicos',          desc: '' },
  { value: 'manual',       label: 'Emails manuais',              desc: '' },
];

export default function AdminEmails() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const inputCls = 'bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none px-3 py-2 text-sm w-full transition-colors';
  const labelCls = 'block text-xs text-[#555] uppercase tracking-widest mb-1';

  const [students, setStudents] = useState([]);
  const [counts, setCounts] = useState({ activeStudents: 0, contacts: 0, total: 0 });
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const [form, setForm] = useState({
    subject: '',
    content: '',
    recipientType: 'all',
    studentIds: [],
    manualEmails: [],
  });
  const [manualInput, setManualInput] = useState({ email: '', name: '' });
  const [studentSearch, setStudentSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  // Confirm modal
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    axios.get(`${BACKEND_URL}/api/admin/students`, { headers }).then(r => setStudents(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/admin/emails/recipient-counts`, { headers }).then(r => setCounts(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/admin/emails/history`, { headers }).then(r => setHistory(r.data)).catch(() => {});
  }, []);

  const getEstimatedCount = () => {
    if (form.recipientType === 'all') return counts.activeStudents;
    if (form.recipientType === 'contacts') return counts.contacts;
    if (form.recipientType === 'all_contacts') return counts.total;
    if (form.recipientType === 'students') return form.studentIds.length;
    if (form.recipientType === 'manual') return form.manualEmails.length;
    return 0;
  };

  const handlePreview = async () => {
    if (!form.content) { setError('Adicione o conteúdo antes de visualizar.'); return; }
    setPreviewing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/emails/preview`, {
        content: form.content, subject: form.subject,
      }, { headers, responseType: 'text' });
      setPreviewHtml(res.data);
    } catch { setError('Erro ao gerar preview.'); }
    finally { setPreviewing(false); }
  };

  const handleSend = () => {
    if (!form.subject) { setError('Adicione o assunto.'); return; }
    if (!form.content) { setError('Adicione o conteúdo.'); return; }
    if (getEstimatedCount() === 0 && form.recipientType !== 'manual') {
      setError('Nenhum destinatário selecionado.'); return;
    }
    setConfirm({
      message: `Enviar email para ${getEstimatedCount()} destinatário(s)?`,
      onConfirm: async () => {
        setSending(true); setError(''); setSent(null);
        try {
          const res = await axios.post(`${BACKEND_URL}/api/admin/emails/send`, form, { headers });
          setSent(res.data.sent);
          setForm({ subject: '', content: '', recipientType: 'all', studentIds: [], manualEmails: [] });
          axios.get(`${BACKEND_URL}/api/admin/emails/history`, { headers }).then(r => setHistory(r.data));
        } catch (err) {
          setError(err.response?.data?.detail || 'Erro ao enviar email.');
        } finally { setSending(false); }
      },
    });
  };

  const toggleStudent = (id) => {
    setForm(f => ({
      ...f,
      studentIds: f.studentIds.includes(id) ? f.studentIds.filter(s => s !== id) : [...f.studentIds, id],
    }));
  };

  const addManualEmail = () => {
    if (!manualInput.email) return;
    setForm(f => ({ ...f, manualEmails: [...f.manualEmails, { ...manualInput }] }));
    setManualInput({ email: '', name: '' });
  };

  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
    s.email.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const formatDate = d => { try { return new Date(d).toLocaleString('pt-BR', { day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit' }); } catch { return d; } };

  const deleteLog = (id) => {
    setConfirm({
      message: 'Apagar este registro do histórico?',
      onConfirm: async () => {
        await axios.delete(`${BACKEND_URL}/api/admin/emails/history/${id}`, { headers });
        setHistory(h => h.filter(l => l.id !== id));
      },
    });
  };

  const clearHistory = () => {
    setConfirm({
      message: 'Apagar todo o histórico de disparos?',
      onConfirm: async () => {
        await axios.delete(`${BACKEND_URL}/api/admin/emails/history`, { headers });
        setHistory([]);
      },
    });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-6 lg:pt-10 pb-20">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/admin')} className="text-[#555] hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold uppercase tracking-tight">E-mails</h1>
            <p className="text-xs text-[#555] mt-0.5">
              {counts.activeStudents} alunos ativos · {counts.contacts} contatos do site
            </p>
          </div>
        </div>

        {/* Success banner */}
        {sent !== null && (
          <div className="mb-6 p-4 bg-green-900/20 border border-green-500/30 text-green-400 text-sm flex items-center justify-between">
            <span>✓ Email enviado para {sent} destinatário(s) com sucesso.</span>
            <button onClick={() => setSent(null)} className="text-[#555] hover:text-white">✕</button>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 text-red-400 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-[#555] hover:text-white">✕</button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT: Compose */}
          <div className="lg:col-span-2 flex flex-col gap-5">

            {/* Subject */}
            <div className="bg-[#121212] border border-white/5 p-5">
              <label className={labelCls}>Assunto</label>
              <input type="text" value={form.subject}
                onChange={e => setForm(f => ({...f, subject: e.target.value}))}
                className={inputCls} placeholder="Ex: Novidades e atualizações — Maio 2025"
              />
            </div>

            {/* Content */}
            <div className="bg-[#121212] border border-white/5 p-5">
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls}>Conteúdo (HTML ou texto)</label>
                <button onClick={handlePreview} disabled={previewing}
                  className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/10 px-2 py-1 transition-colors"
                >
                  <Eye size={12} /> {previewing ? 'Gerando...' : 'Preview'}
                </button>
              </div>
              <textarea
                value={form.content}
                onChange={e => setForm(f => ({...f, content: e.target.value}))}
                className={`${inputCls} min-h-[240px] resize-y font-mono text-xs`}
                placeholder={`<p>Olá,</p>\n<p>Seu texto aqui...</p>\n<p>Abraços,<br>Daniel</p>`}
              />
              <p className="text-xs text-[#555] mt-2">
                O cabeçalho e rodapé com a marca Daniel Aron são adicionados automaticamente.
              </p>
            </div>

            {/* Preview iframe */}
            {previewHtml && (
              <div className="bg-[#121212] border border-white/5 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-[#A3A3A3] uppercase tracking-widest">Preview do email</span>
                  <button onClick={() => setPreviewHtml('')} className="text-[#555] hover:text-white"><X size={14}/></button>
                </div>
                <iframe
                  srcDoc={previewHtml}
                  className="w-full border border-white/5"
                  style={{ height: '500px' }}
                  title="Email Preview"
                />
              </div>
            )}
          </div>

          {/* RIGHT: Recipients + Send */}
          <div className="flex flex-col gap-4">

            {/* Recipient type */}
            <div className="bg-[#121212] border border-white/5 p-5">
              <label className={labelCls}>Destinatários</label>
              <div className="flex flex-col gap-2 mt-2">
                {RECIPIENT_TYPES.map(rt => (
                  <button key={rt.value}
                    onClick={() => setForm(f => ({...f, recipientType: rt.value}))}
                    className={`flex items-center justify-between px-3 py-2.5 border text-left transition-colors text-sm ${form.recipientType === rt.value ? 'border-white/30 bg-white/5 text-white' : 'border-white/5 text-[#555] hover:text-white hover:border-white/10'}`}
                  >
                    <span>{rt.label}</span>
                    {form.recipientType === rt.value && (
                      <span className="text-xs text-[#A3A3A3]">{getEstimatedCount()}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Student selector */}
            {form.recipientType === 'students' && (
              <div className="bg-[#121212] border border-white/5 p-5">
                <label className={labelCls}>Selecionar alunos ({form.studentIds.length})</label>
                <input type="text" placeholder="Buscar..." value={studentSearch}
                  onChange={e => setStudentSearch(e.target.value)}
                  className={`${inputCls} mb-2`}
                />
                <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                  {filteredStudents.map(s => (
                    <button key={s.id} onClick={() => toggleStudent(s.id)}
                      className={`flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors border ${form.studentIds.includes(s.id) ? 'border-white/20 bg-white/5 text-white' : 'border-white/5 text-[#555] hover:text-white'}`}
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${form.studentIds.includes(s.id) ? 'bg-white' : 'bg-[#333]'}`} />
                      <span className="truncate">{s.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Manual emails */}
            {form.recipientType === 'manual' && (
              <div className="bg-[#121212] border border-white/5 p-5">
                <label className={labelCls}>Adicionar emails</label>
                <div className="flex gap-2 mb-3">
                  <input type="email" placeholder="email@exemplo.com" value={manualInput.email}
                    onChange={e => setManualInput(m => ({...m, email: e.target.value}))}
                    className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm flex-1 outline-none"
                    onKeyDown={e => e.key === 'Enter' && addManualEmail()}
                  />
                  <button onClick={addManualEmail} className="px-3 py-2 bg-white text-black text-xs font-bold">
                    <Plus size={14} />
                  </button>
                </div>
                <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                  {form.manualEmails.map((e, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-[#0A0A0A] border border-white/5 text-xs">
                      <span className="truncate text-[#A3A3A3]">{e.email}</span>
                      <button onClick={() => setForm(f => ({...f, manualEmails: f.manualEmails.filter((_,j)=>j!==i)}))} className="text-[#555] hover:text-red-400 ml-2 flex-shrink-0">
                        <X size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Send button */}
            <button onClick={handleSend} disabled={sending}
              className="w-full py-3 bg-white text-black font-bold uppercase tracking-widest text-sm hover:bg-[#E5E5E5] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Send size={15} />
              {sending ? 'Enviando...' : `Enviar${getEstimatedCount() > 0 ? ` para ${getEstimatedCount()}` : ''}`}
            </button>
          </div>
        </div>

        {/* History */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setShowHistory(v => !v)}
              className="flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-widest transition-colors"
            >
              <Clock size={13} /> Histórico de disparos ({history.length}/10)
              {showHistory ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
            </button>
            {history.length > 0 && (
              <button onClick={clearHistory}
                className="flex items-center gap-1 text-xs text-[#555] hover:text-red-400 transition-colors"
                title="Apagar todo o histórico"
              >
                <Trash2 size={13} /> Limpar tudo
              </button>
            )}
          </div>
          {showHistory && (
            <div className="flex flex-col gap-2">
              {history.length === 0 ? (
                <div className="text-center py-10 text-[#555] text-sm bg-[#121212] border border-white/5">
                  Nenhum email enviado ainda.
                </div>
              ) : history.map(log => (
                <div key={log.id} className="bg-[#121212] border border-white/5 px-5 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{log.subject}</p>
                    {log.content && <p className="text-xs text-[#555] mt-0.5 truncate">{log.content}...</p>}
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className={`text-xs border px-2 py-0.5 ${log.status === 'sent' ? 'border-green-500/30 text-green-400' : 'border-red-500/30 text-red-400'}`}>
                      {log.status === 'sent' ? `✓ ${log.count}` : '✕ falha'}
                    </span>
                    <span className="text-xs text-[#555]">{formatDate(log.sentAt)}</span>
                    <button onClick={() => deleteLog(log.id)}
                      className="text-[#555] hover:text-red-400 transition-colors"
                      title="Apagar este registro"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Confirm modal */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm(null)} />
          <div className="relative bg-[#121212] border border-white/10 p-6 w-full max-w-sm">
            <p className="text-sm text-[#E5E5E5] mb-6 leading-relaxed">{confirm.message}</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirm(null)} className="px-4 py-2 text-sm text-[#555] hover:text-white transition-colors">Cancelar</button>
              <button onClick={() => { confirm.onConfirm(); setConfirm(null); }}
                className="px-4 py-2 bg-white text-black text-sm font-bold hover:bg-[#E5E5E5] transition-colors">
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
